/**
 * WeaveIn.ai Platform - Contact Types & Pipelines Admin Page
 * Allows admins to select a program and configure its contact lifecycle pipeline
 */

import React, { useState, useEffect } from 'react';
import { Users, ChevronRight, Loader2 } from 'lucide-react';
import { programsAPI } from '@/services/api/programs';
import ProgramContactStatusConfig from './ProgramContactStatusConfig';
import { useTabbedChrome } from '@/components/weavein';

export default function ContactTypesPage() {
  const tabbed = useTabbedChrome();
  const [programs, setPrograms] = useState<Array<{ id: number; name: string; program_type: string }>>([]);
  const [selectedProgram, setSelectedProgram] = useState<{ id: number; name: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await programsAPI.listPrograms();
        const list = Array.isArray(data) ? data : (data as any)?.programs || [];
        setPrograms(list.map((p: any) => ({ id: p.id, name: p.name, program_type: p.program_type })));
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, []);

  return (
    <div className="p-6 space-y-6">
      <div>
        {!tabbed && (
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Users className="w-6 h-6 text-blue-600" />
          Contact Types & Pipelines
        </h1>
        )}
        <p className="text-sm text-gray-500 mt-1">
          Configure contact lifecycle statuses per program. Each status defines required fields,
          allowed transitions, auto-workflows, and promotion rules.
        </p>
      </div>

      {!selectedProgram ? (
        <div>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Select a Program</h2>
          {loading ? (
            <div className="flex items-center gap-2 py-8 justify-center text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading programs...
            </div>
          ) : programs.length === 0 ? (
            <p className="text-sm text-gray-400 py-4">No programs found. Create a program first.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {programs.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedProgram(p)}
                  className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 hover:shadow-sm transition-all text-left"
                >
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{p.name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{p.program_type}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          <button
            onClick={() => setSelectedProgram(null)}
            className="text-sm text-blue-600 hover:underline mb-4 flex items-center gap-1"
          >
            &larr; Back to Program List
          </button>
          <ProgramContactStatusConfig
            programId={selectedProgram.id}
            programName={selectedProgram.name}
          />
        </div>
      )}
    </div>
  );
}
